import React, { useState, useEffect, useLayoutEffect } from 'react';

// 배경 사진(public/background-hero.png, 1536×1024) 속 랩탑 화면(베젤 안쪽)의 네 모서리 — 사진을 분석해 네 변을 직선으로 맞춘
// 교점(왼쪽 위·오른쪽 위·오른쪽 아래·왼쪽 아래, 사진 픽셀 좌표). 배경 사진을 바꾸면 다시 구한다 (CLAUDE.md Phase 20)
const HERO_IMAGE = { width: 1536, height: 1024 };
const LAPTOP_SCREEN = [[690.0, 463.0], [1097.5, 393.2], [1117.0, 672.8], [721.7, 755.2]];
// 터미널을 그리는 기준 크기(랩탑 화면과 같은 비율). 이 상자의 네 모서리를 랩탑 화면의 네 점에 원근 변환으로 겹친다
const TERMINAL_BOX = { width: 420, height: 295 };

// 직사각형 (0,0)-(w,h) 의 네 모서리를 네 점 p0~p3(왼쪽 위부터 시계 방향)으로 보내는 원근 변환(호모그래피) → CSS matrix3d
const rectToQuadMatrix3d = (w, h, [[x0, y0], [x1, y1], [x2, y2], [x3, y3]]) => {
  const dx1 = x1 - x2;
  const dx2 = x3 - x2;
  const dx3 = x0 - x1 + x2 - x3;
  const dy1 = y1 - y2;
  const dy2 = y3 - y2;
  const dy3 = y0 - y1 + y2 - y3;
  const den = dx1 * dy2 - dx2 * dy1;
  const g = (dx3 * dy2 - dx2 * dy3) / den;
  const k = (dx1 * dy3 - dx3 * dy1) / den;
  const a = x1 - x0 + g * x1;
  const b = x3 - x0 + k * x3;
  const d = y1 - y0 + g * y1;
  const e = y3 - y0 + k * y3;
  // 단위 정사각형 기준 계수를 w×h 상자 기준으로 바꿔(u = x/w, v = y/h) 열 우선 순서로 쓴다
  return `matrix3d(${[a / w, d / w, 0, g / w, b / h, e / h, 0, k / h, 0, 0, 1, 0, x0, y0, 0, 1].join(',')})`;
};

const HeroSection = ({ typingTexts }) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTyping, setIsTyping] = useState(true);
  const [terminalTransform, setTerminalTransform] = useState(null);

  // 배경 사진이 지금 화면에 깔린 크기(object-cover, 가운데 기준)를 재서 사진 속 랩탑 화면의 네 점을 구하고,
  // 터미널 상자를 그 네 점에 겹치는 변환을 만든다. 창 크기·회전·모바일 주소창이 바뀌면 다시 계산한다 (CLAUDE.md Phase 20)
  useLayoutEffect(() => {
    const background = document.getElementById('hero_background');
    const update = () => {
      const rect = background
        ? background.getBoundingClientRect()
        : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
      if (!rect.width || !rect.height) return;
      const scale = Math.max(rect.width / HERO_IMAGE.width, rect.height / HERO_IMAGE.height);
      const offsetX = rect.left + (rect.width - HERO_IMAGE.width * scale) / 2;
      const offsetY = rect.top + (rect.height - HERO_IMAGE.height * scale) / 2;
      const quad = LAPTOP_SCREEN.map(([x, y]) => [offsetX + x * scale, offsetY + y * scale]);
      setTerminalTransform(rectToQuadMatrix3d(TERMINAL_BOX.width, TERMINAL_BOX.height, quad));
    };
    update();
    window.addEventListener('resize', update);
    const observer = background && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (observer) observer.observe(background);
    return () => {
      window.removeEventListener('resize', update);
      if (observer) observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const speed = 100;
    const deleteSpeed = 50;
    const pauseTime = 2000;
    
    const timeout = setTimeout(() => {
      if (isTyping && !isDeleting) {
        const fullText = typingTexts[currentTextIndex];
        if (currentText.length < fullText.length) {
          setCurrentText(fullText.substring(0, currentText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), pauseTime);
        }
      } else if (isDeleting) {
        if (currentText.length > 0) {
          setCurrentText(currentText.substring(0, currentText.length - 1));
        } else {
          setIsDeleting(false);
          setCurrentTextIndex((prev) => (prev + 1) % typingTexts.length);
        }
      }
    }, isDeleting ? deleteSpeed : speed);

    return () => clearTimeout(timeout);
  }, [currentText, currentTextIndex, isDeleting, isTyping, typingTexts]);

  // mission 문자열은 따옴표까지 하나로 합쳐 <span> 의 유일한 자식으로 둔다(React 가 textContent 로 갱신).
  // 조각으로 나누면 지우기가 끝나 '' 가 되는 순간 React 가 텍스트 노드를 지우는데, 브라우저 번역이 그 노드를
  // <font> 로 바꿔 놓았으면 NotFoundError 로 흰 화면이 된다 (CLAUDE.md Phase 8)
  const missionText = `"${currentText.length > 15 ? currentText.slice(0, 15) + '...' : currentText}"`;

  return (
    <div className="relative w-full h-96 lg:h-[500px]">
      {/* 맥북 스크린 오버레이 - 타이핑 효과. 420×295 기준 상자를 원근 변환으로 배경 사진 속 랩탑 화면에 정확히 겹친다(변환을 구하기 전엔 숨김) */}
      <div
        className="fixed bg-gray-900/95 backdrop-blur-sm rounded-md overflow-hidden flex flex-col shadow-2xl border border-gray-800"
        style={{
          left: 0,
          top: 0,
          width: `${TERMINAL_BOX.width}px`,
          height: `${TERMINAL_BOX.height}px`,
          transformOrigin: '0 0',
          transform: terminalTransform || undefined,
          visibility: terminalTransform ? 'visible' : 'hidden',
        }}
      >
        {/* Terminal Header */}
        <div className="flex items-center gap-1 px-2 py-1 bg-gray-800 border-b border-gray-700">
          <div className="flex gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500"></div>
            <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
          </div>
          <span className="text-gray-400 text-xs ml-2 font-mono">jiyoun@dev:~$</span>
        </div>

        {/* Code Content - 타이핑 효과 */}
        <div className="flex-1 p-3 font-mono text-sm space-y-1 overflow-hidden">
          <div className="text-green-400">
            <span className="text-blue-400">const</span> developer = {'{'}
          </div>
          <div className="text-green-400 ml-2">
            name: <span className="text-yellow-300">"이지연 Jiyoun Lee"</span>,
          </div>
          <div className="text-green-400 ml-2">
            role: <span className="text-yellow-300">"Developer"</span>,
          </div>
          <div className="text-green-400 ml-2">
            mission: <span className="text-yellow-300">{missionText}</span>
            <span className="animate-pulse text-white">|</span>
          </div>
          <div className="text-green-400">
            {'}'};
          </div>

          <div className="text-gray-400">
            <span className="text-blue-400">console</span>.log(developer.mission);
          </div>

          <div className="text-yellow-300 animate-pulse">
            // 실행 중...
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
